import { NavLink } from 'react-router-dom';
import type { MangaItem } from '@/mocks/manga';

interface MangaCardProps {
  manga: MangaItem;
  size?: 'normal' | 'small';
}

export default function MangaCard({ manga, size = 'normal' }: MangaCardProps) {
  if (size === 'small') {
    return (
      <NavLink
        to={`/article/${manga.id}`}
        className="group flex items-center gap-3 p-2 rounded-xl hover:bg-background-100 transition-colors"
        data-product-shop="true"
      >
        <div className="w-14 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
          <img
            src={manga.coverImage}
            alt={`${manga.title} 漫画封面`}
            title={`${manga.title} - 日本漫画在线阅读`}
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-medium text-foreground-950 truncate group-hover:text-accent-500 transition-colors">
            {manga.title}
          </h4>
          <p className="text-xs text-foreground-600 mt-0.5">{manga.author}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="flex items-center gap-0.5 text-xs text-yellow-500">
              <i className="ri-star-fill text-[10px]"></i>
              {manga.rating}
            </span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${
              manga.status === '连载中'
                ? 'bg-accent-100/80 text-accent-700'
                : 'bg-background-200 text-foreground-600'
            }`}>
              {manga.status}
            </span>
          </div>
        </div>
      </NavLink>
    );
  }

  return (
    <NavLink
      to={`/article/${manga.id}`}
      className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-accent-300 transition-all duration-300"
      data-product-shop="true"
    >
      <div className="aspect-[3/4] overflow-hidden bg-background-200 relative">
        <img
          src={manga.coverImage}
          alt={`${manga.title} 漫画封面`}
          title={`${manga.title} - 日本漫画在线阅读`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-2 left-2">
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
            manga.status === '连载中'
              ? 'bg-accent-500 text-background-50'
              : 'bg-foreground-700/80 text-background-50'
          }`}>
            {manga.status}
          </span>
        </div>
        {manga.featured && (
          <div className="absolute top-2 right-2">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-50/90 text-accent-600 font-medium whitespace-nowrap">
              推荐
            </span>
          </div>
        )}
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold text-foreground-950 truncate group-hover:text-accent-500 transition-colors">
          {manga.title}
        </h3>
        <p className="text-xs text-foreground-600 mt-0.5">{manga.author}</p>
        <div className="flex items-center justify-between mt-2">
          <span className="flex items-center gap-0.5 text-xs text-yellow-500">
            <i className="ri-star-fill text-[10px]"></i>
            {manga.rating}
          </span>
          <span className="text-[11px] text-foreground-600 whitespace-nowrap">
            {manga.chapters}话
          </span>
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {manga.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="text-[10px] px-1.5 py-0.5 rounded bg-background-100 text-foreground-600 whitespace-nowrap"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </NavLink>
  );
}